import { useState } from "react";
import { AlertTriangle, CheckCircle, ChevronDown, ChevronRight, Video, BarChart3 } from "lucide-react";

interface LessonSummary {
  lesson_id: string;
  lesson_name: string;
  course_name: string;
  lesson_order: number;
  lesson_video_url: string | null;
  lesson_infographic_url: string | null;
}

interface MissingLessonMediaPanelProps {
  lessons: LessonSummary[];
  mediaType: "video" | "infographic";
}

export default function MissingLessonMediaPanel({ lessons, mediaType }: MissingLessonMediaPanelProps) {
  const [expanded, setExpanded] = useState(false);

  // Build course order from minimum lesson_order per course
  const courseOrderMap = lessons.reduce<Record<string, number>>((acc, lesson) => {
    if (acc[lesson.course_name] === undefined || lesson.lesson_order < acc[lesson.course_name]) {
      acc[lesson.course_name] = lesson.lesson_order;
    }
    return acc;
  }, {});

  const courseMap = lessons.reduce<Record<string, { total: number; missing: string[] }>>((acc, lesson) => {
    if (!acc[lesson.course_name]) acc[lesson.course_name] = { total: 0, missing: [] };
    acc[lesson.course_name].total += 1;

    const missingMedia = mediaType === "video" ? !lesson.lesson_video_url : !lesson.lesson_infographic_url;
    if (missingMedia) acc[lesson.course_name].missing.push(lesson.lesson_name);

    return acc;
  }, {});

  const coursesWithMissing = Object.entries(courseMap)
    .filter(([, value]) => value.missing.length > 0)
    .sort((a, b) => (courseOrderMap[a[0]] ?? 999) - (courseOrderMap[b[0]] ?? 999));

  const totalMissing = coursesWithMissing.reduce((sum, [, value]) => sum + value.missing.length, 0);
  const mediaLabel = mediaType === "video" ? "videos" : "infographics";
  const MediaIcon = mediaType === "video" ? Video : BarChart3;

  if (coursesWithMissing.length === 0) {
    return (
      <section className="rounded-xl border border-accent/30 bg-accent/10 p-4 flex items-center gap-2">
        <CheckCircle className="w-4 h-4 text-accent shrink-0" />
        <p className="text-xs font-body text-accent">All courses have {mediaLabel} assigned to every lesson.</p>
      </section>
    );
  }

  return (
    <section className="rounded-xl border border-secondary/30 bg-secondary/10 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 p-4 text-left hover:bg-secondary/10 transition-colors"
      >
        <AlertTriangle className="w-4 h-4 text-secondary shrink-0" />
        <MediaIcon className="w-4 h-4 text-secondary shrink-0" />
        <span className="text-xs font-heading font-semibold text-secondary flex-1">
          {coursesWithMissing.length} course{coursesWithMissing.length !== 1 ? "s" : ""} missing {mediaLabel} ({totalMissing} lesson{totalMissing !== 1 ? "s" : ""} total)
        </span>
        {expanded ? <ChevronDown className="w-4 h-4 text-secondary" /> : <ChevronRight className="w-4 h-4 text-secondary" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 max-h-80 overflow-y-auto">
          {coursesWithMissing.map(([course, { total, missing }]) => (
            <div key={course} className="space-y-1">
              <div className="flex items-center justify-between">
                <p className="text-xs font-heading font-medium text-foreground">{course}</p>
                <span className="text-[10px] font-body text-secondary">
                  {missing.length}/{total} missing
                </span>
              </div>
              <div className="flex flex-wrap gap-1">
                {missing.map((name) => (
                  <span key={`${course}-${name}`} className="text-[10px] font-body text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-full">
                    {name}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
